import type { CloudCostInvoice, CloudCostLine } from "@hasheeai/types";
export type CostCategory =
  | "compute"
  | "storage"
  | "requests"
  | "network"
  | "other";
export const costColors = {
  fixed: "#334f83",
  compute: "#158579",
  storage: "#d49a31",
  requests: "#8461bd",
  network: "#3a91b8",
  other: "#7c8798",
} as const;
export function costCategory(service: string): CostCategory {
  const name = service.toLowerCase();
  if (
    /bytes_month|storage|restore|snapshot/.test(name) &&
    !/reads|writes|deletes|operations|rows/.test(name)
  )
    return "storage";
  if (/requests|operations|invocations|reads|writes|deletes|rows/.test(name))
    return "requests";
  if (/compute|capacity|duration|cpu/.test(name)) return "compute";
  if (/network|transfer|egress/.test(name)) return "network";
  return "other";
}
export function pricedTotal(lines: CloudCostLine[]): number {
  return lines.reduce(
    (total, line) =>
      total + (line.currency.toUpperCase() === "USD" ? (line.amount ?? 0) : 0),
    0,
  );
}
export function categoryTotals(
  lines: CloudCostLine[],
): { category: CostCategory; amount: number }[] {
  const sums = new Map<CostCategory, number>();
  for (const line of lines) {
    if (line.currency.toUpperCase() !== "USD" || line.amount === null) continue;
    const category = costCategory(line.service);
    sums.set(category, (sums.get(category) ?? 0) + line.amount);
  }
  return [...sums].map(([category, amount]) => ({ category, amount }));
}
export function utcDate(value: string): string {
  return new Date(value).toISOString().replace("T", " ").slice(0, 19);
}
export function monthOptions(today: string): string[] {
  const date = new Date(`${today.slice(0, 7)}-01T00:00:00Z`);
  return Array.from({ length: 12 }, (_, index) =>
    new Date(Date.UTC(date.getUTCFullYear(), date.getUTCMonth() - index, 1))
      .toISOString()
      .slice(0, 7),
  );
}
export function datesInMonth(month: string, today: string): string[] {
  const end = new Date(
    Date.UTC(Number(month.slice(0, 4)), Number(month.slice(5, 7)), 0),
  ).getUTCDate();
  return Array.from(
    { length: end },
    (_, index) => `${month}-${String(index + 1).padStart(2, "0")}`,
  ).filter((date) => date <= today);
}
export function isDaily(line: CloudCostLine): boolean {
  return Date.parse(line.end) - Date.parse(line.start) <= 86400000;
}
export function selectedCostDay(
  month: string,
  lines: CloudCostLine[],
  today: string,
): string {
  if (month === today.slice(0, 7)) return today;
  const dates = lines
    .filter(isDaily)
    .map((line) => line.start.slice(0, 10))
    .filter((date) => date.startsWith(month))
    .sort();
  return dates.at(-1) ?? datesInMonth(month, today).at(-1) ?? `${month}-01`;
}

export function invoiceGroups(invoices: CloudCostInvoice[]): {
  category: ReturnType<typeof costCategory> | "fixed" | "tax" | "credit";
  amount: number;
}[] {
  const groups = new Map<
    ReturnType<typeof costCategory> | "fixed" | "tax" | "credit",
    number
  >();
  for (const invoice of invoices) {
    if (invoice.currency.toUpperCase() !== "USD") continue;
    for (const item of invoice.items) {
      const category =
        item.amount < 0
          ? "credit"
          : /tax|gst/i.test(item.description)
            ? "tax"
            : /Workers Paid|Workers for Platforms/i.test(item.description)
              ? "fixed"
              : costCategory(item.description);
      groups.set(category, (groups.get(category) ?? 0) + item.amount);
    }
    const remainder =
      invoice.total - invoice.items.reduce((sum, item) => sum + item.amount, 0);
    if (Math.abs(remainder) > 0.000001)
      groups.set("other", (groups.get("other") ?? 0) + remainder);
  }
  return [...groups].map(([category, amount]) => ({ category, amount }));
}
